import { useMemo } from "react";
import { getTimeZoneOptions, timeZoneLabel } from "../utils/timeZones";

// The select's id is already fixed ("time_zone"), and each form renders the
// field once, so the error's id can be fixed too.
const ERROR_ID = "time_zone-error";

// The store time zone select, shared by Settings (where merchants edit their
// store), EditStore and CreateStore (setup wizard). On edit a store with no
// zone shows an empty select and stays unset until one is picked and the form
// saved — no browser suggestion there (Serhat, 2026-10-21: a pre-filled zone
// looked as if it had already been set). CreateStore pre-fills the browser
// zone itself and marks the field required (Serhat, 2026-10-21).
//
// `savedTimeZone` is kept in the option list even if this runtime's Intl list
// names it differently (the store's saved zone, or CreateStore's pre-fill).
// `required` / `error` only change how the field is shown; the form validates.
export default function StoreTimeZoneField({
  value,
  onChange,
  savedTimeZone,
  t,
  required = false,
  error = "",
}) {
  const options = useMemo(
    () => getTimeZoneOptions(savedTimeZone),
    [savedTimeZone],
  );

  return (
    <div>
      <label
        htmlFor="time_zone"
        className="block text-sm font-medium text-gray-700"
      >
        {t("store.timeZone")}
      </label>
      <div className="mt-1">
        <select
          id="time_zone"
          name="time_zone"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? ERROR_ID : undefined}
          className={`block w-full appearance-none rounded-md border px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 focus:outline-hidden sm:text-sm ${
            error ? "border-red-300" : "border-gray-300"
          }`}
        >
          {!value && <option value="" disabled />}
          {options.map((zone) => (
            <option key={zone} value={zone}>
              {timeZoneLabel(zone)}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <p id={ERROR_ID} className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
